import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Settings, 
  Users, 
  FileSpreadsheet, 
  Bell, 
  Shield,
  Plus,
  Trash2,
  CheckSquare,
} from 'lucide-react';
import { projectsApi, checklistSettingsApi, ChecklistSettings, rejectionReasonsApi, RejectionReason } from '../lib/api';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Badge } from '../components/ui/Badge';
import { LoadingSpinner } from '../components/LoadingScreen';
import { useTelegram } from '../hooks/useTelegram';

const roleOptions = [
  { value: 'ADMIN', label: '👑 Администратор' },
  { value: 'MANAGER', label: '📋 Руководитель' },
  { value: 'EXECUTOR', label: '👷 Исполнитель' },
  { value: 'OBSERVER', label: '👁 Наблюдатель' },
];

const kpiModeOptions = [
  { value: 'EACH_FULL', label: 'Каждому +1 за задачу' },
  { value: 'SPLIT_EQUAL', label: 'Делить между ответственными' },
];

export function SettingsPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hapticFeedback, showAlert, showConfirm } = useTelegram();

  const [activeTab, setActiveTab] = useState<'general' | 'members' | 'sheets' | 'reminders' | 'checklists'>('general');
  const [showAddMember, setShowAddMember] = useState(false);
  const [newMember, setNewMember] = useState({
    position: '',
    firstName: '',
    lastName: '',
    username: '',
    role: 'EXECUTOR',
  });
  const [editingMemberId, setEditingMemberId] = useState<string | null>(null);
  const [editingMemberData, setEditingMemberData] = useState<{
    firstName: string;
    lastName: string;
    username: string;
    position: string;
    role: string;
  } | null>(null);

  const { data: project, isLoading } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => projectsApi.get(projectId!),
    enabled: !!projectId,
  });

  // Настройки чек-листов проекта
  const { data: checklistSettings } = useQuery({
    queryKey: ['checklist-settings', projectId],
    queryFn: () => checklistSettingsApi.get(projectId!),
    enabled: !!projectId,
  });

  const updateProjectMutation = useMutation({
    mutationFn: (data: any) => projectsApi.update(projectId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
      hapticFeedback('success');
      showAlert('Настройки сохранены');
    },
    onError: (err: Error) => {
      hapticFeedback('error');
      showAlert(`Ошибка: ${err.message}`);
    },
  });

  const addMemberMutation = useMutation({
    mutationFn: (data: { username?: string; firstName?: string; lastName?: string; position?: string; role: string }) => 
      projectsApi.addMember(projectId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
      hapticFeedback('success');
      showAlert('Участник добавлен');
      setShowAddMember(false);
      setNewMember({ position: '', firstName: '', lastName: '', username: '', role: 'EXECUTOR' });
    },
    onError: (err: Error) => {
      hapticFeedback('error');
      showAlert(`Ошибка: ${err.message}`);
    },
  });

  const updateMemberMutation = useMutation({
    mutationFn: ({ userId, data }: { userId: string; data: {
      firstName?: string;
      lastName?: string;
      username?: string;
      position?: string;
      role?: string;
    }}) => 
      projectsApi.updateMember(projectId!, userId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
      hapticFeedback('success');
      showAlert('Данные участника обновлены');
      setEditingMemberId(null);
      setEditingMemberData(null);
    },
    onError: (err: Error) => {
      hapticFeedback('error');
      showAlert(`Ошибка: ${err.message}`);
    },
  });

  const removeMemberMutation = useMutation({
    mutationFn: (userId: string) => projectsApi.removeMember(projectId!, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
      hapticFeedback('success');
      showAlert('Участник удалён');
    },
    onError: (err: Error) => {
      hapticFeedback('error');
      showAlert(`Ошибка: ${err.message}`);
    },
  });

  const deleteProjectMutation = useMutation({
    mutationFn: () => projectsApi.delete(projectId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      hapticFeedback('success');
      showAlert('Проект удалён');
      navigate('/projects');
    },
    onError: (err: Error) => {
      hapticFeedback('error');
      showAlert(`Ошибка: ${err.message}`);
    },
  });

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    googleSheetId: '',
    kpiSplitMode: 'EACH_FULL',
  });

  const [checklistSettingsForm, setChecklistSettingsForm] = useState<ChecklistSettings | null>(null);

  const checklistSettingsMutation = useMutation({
    mutationFn: (data: Partial<ChecklistSettings>) =>
      checklistSettingsApi.update(projectId!, data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['checklist-settings', projectId] });
      setChecklistSettingsForm(data);
      hapticFeedback('success');
      showAlert('Настройки чек-листов сохранены');
    },
    onError: (err: Error) => {
      hapticFeedback('error');
      showAlert(`Ошибка: ${err.message}`);
    },
  });

  // Синхронизируем форму с данными проекта
  useEffect(() => {
    if (project) {
      setFormData({
        name: project.name || '',
        description: project.description || '',
        googleSheetId: project.googleSheetId || '',
        kpiSplitMode: project.kpiSplitMode || 'EACH_FULL',
      });
    }
  }, [project]);

  // Синхронизируем форму настроек чек-листов
  useEffect(() => {
    if (checklistSettings) {
      setChecklistSettingsForm(checklistSettings);
    }
  }, [checklistSettings]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="p-4 text-center">
        <p className="text-tg-hint">Проект не найден</p>
      </div>
    );
  }

  const isAdmin = project.role === 'ADMIN';

  return (
    <div className="p-4 space-y-4">
      {/* Заголовок */}
      <div className="flex items-center gap-2">
        <Settings className="w-6 h-6 text-tg-button" />
        <h1 className="text-xl font-bold text-tg-text">Настройки проекта</h1>
      </div>

      {/* Табы */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {[
          { id: 'general', label: 'Основные', icon: Settings },
          { id: 'members', label: 'Участники', icon: Users },
          { id: 'sheets', label: 'Google Sheets', icon: FileSpreadsheet },
          { id: 'reminders', label: 'Напоминания', icon: Bell },
          { id: 'checklists', label: 'Чек-листы', icon: CheckSquare },
    { id: 'rejection-reasons', label: 'Причины отклонения', icon: Shield },
        ].map((tab) => (
          <Button
            key={tab.id}
            variant={activeTab === tab.id ? 'primary' : 'secondary'}
            size="sm"
            onClick={() => {
              hapticFeedback('light');
              setActiveTab(tab.id as any);
            }}
            className="flex-shrink-0 gap-1"
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </Button>
        ))}
      </div>

      {/* Основные настройки */}
      {activeTab === 'general' && (
        <Card className="p-4 space-y-4">
          <Input
            label="Название проекта"
            value={formData.name || project.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            disabled={!isAdmin}
          />
          <Input
            label="Описание"
            value={formData.description || project.description || ''}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            disabled={!isAdmin}
          />
          <Select
            label="Режим расчёта KPI"
            value={formData.kpiSplitMode || project.kpiSplitMode}
            onChange={(e) => setFormData({ ...formData, kpiSplitMode: e.target.value })}
            options={kpiModeOptions}
            disabled={!isAdmin}
          />
          {isAdmin && (
            <Button
              onClick={() => updateProjectMutation.mutate(formData)}
              isLoading={updateProjectMutation.isPending}
            >
              Сохранить
            </Button>
          )}
        </Card>
      )}

      {/* Участники */}
      {activeTab === 'members' && (
        <div className="space-y-4">
          <Card className="p-4">
              {showAddMember ? (
                <div className="space-y-3">
                  <p className="font-medium text-tg-text">Новый участник</p>
                  
                  <Input
                    label="Должность"
                    placeholder="Прораб, Инженер ПТО, Снабженец..."
                    value={newMember.position}
                    onChange={(e) => setNewMember({ ...newMember, position: e.target.value })}
                  />
                  
                  <div className="grid grid-cols-2 gap-2">
                    <Input
                      label="Фамилия"
                      placeholder="Иванов"
                      value={newMember.lastName}
                      onChange={(e) => setNewMember({ ...newMember, lastName: e.target.value })}
                    />
                    <Input
                      label="Имя *"
                      placeholder="Иван"
                      value={newMember.firstName}
                      onChange={(e) => setNewMember({ ...newMember, firstName: e.target.value })}
                    />
                  </div>
                  
                  <Input
                    label="Telegram"
                    placeholder="@username"
                    value={newMember.username}
                    onChange={(e) => setNewMember({ ...newMember, username: e.target.value })}
                  />
                  
                  <Select
                    label="Права доступа"
                    value={newMember.role}
                    onChange={(e) => setNewMember({ ...newMember, role: e.target.value })}
                    options={isAdmin ? roleOptions : roleOptions.filter(r => r.value !== 'ADMIN')}
                  />
                  
                  <div className="flex gap-2 pt-2">
                    <Button
                      onClick={() => {
                        addMemberMutation.mutate({
                          position: newMember.position || undefined,
                          firstName: newMember.firstName || undefined,
                          lastName: newMember.lastName || undefined,
                          username: newMember.username.replace('@', '') || undefined,
                          role: newMember.role,
                        });
                      }}
                      disabled={!newMember.firstName.trim() && !newMember.username.trim()}
                      isLoading={addMemberMutation.isPending}
                      className="flex-1"
                    >
                      Добавить
                    </Button>
                    <Button
                      variant="secondary"
                      onClick={() => {
                        setShowAddMember(false);
                        setNewMember({ position: '', firstName: '', lastName: '', username: '', role: 'EXECUTOR' });
                      }}
                    >
                      Отмена
                    </Button>
                  </div>
                </div>
              ) : (
                <Button
                  variant="outline"
                  onClick={() => setShowAddMember(true)}
                  className="w-full gap-1"
                >
                  <Plus className="w-4 h-4" />
                  Добавить участника
                </Button>
              )}
            </Card>

          <Card>
            <div className="divide-y divide-tg-bg">
              {(project as any).members?.length === 0 && (
                <div className="p-4 text-center text-tg-hint">
                  Нет участников
                </div>
              )}
              {(project as any).members?.map((member: any) => {
                const isEditing = editingMemberId === member.id;
                const isCurrentUser = false; // TODO: добавить проверку текущего пользователя
                
                return (
                  <div key={member.id} className="p-4">
                    {!isEditing ? (
                      <>
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            {/* Должность */}
                            {member.position && (
                              <p className="text-xs font-medium text-blue-600 mb-1">{member.position}</p>
                            )}
                            {/* ФИО */}
                            <p className="font-medium text-tg-text">
                              {member.lastName || ''} {member.firstName}
                            </p>
                            {/* Telegram */}
                            {member.username && (
                              <p className="text-sm text-tg-hint">@{member.username}</p>
                            )}
                          </div>
                          <Badge
                            variant={
                              member.role === 'ADMIN' ? 'danger' :
                              member.role === 'MANAGER' ? 'info' :
                              member.role === 'EXECUTOR' ? 'success' : 'default'
                            }
                          >
                            {roleOptions.find(r => r.value === member.role)?.label.split(' ')[1] || member.role}
                          </Badge>
                        </div>
                        
                        {/* Кнопки управления для админа */}
                        {isAdmin && (
                          <div className="flex gap-2 mt-3">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setEditingMemberId(member.id);
                                setEditingMemberData({
                                  firstName: member.firstName || '',
                                  lastName: member.lastName || '',
                                  username: member.username || '',
                                  position: member.position || '',
                                  role: member.role,
                                });
                              }}
                            >
                              Редактировать
                            </Button>
                            {!isCurrentUser && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  if (confirm(`Удалить ${member.firstName} ${member.lastName || ''} из проекта?`)) {
                                    removeMemberMutation.mutate(member.id);
                                  }
                                }}
                                className="text-red-500 hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="space-y-3">
                        <Input
                          label="Должность"
                          placeholder="Прораб, Инженер ПТО..."
                          value={editingMemberData?.position || ''}
                          onChange={(e) => setEditingMemberData({
                            ...editingMemberData!,
                            position: e.target.value,
                          })}
                        />
                        <div className="grid grid-cols-2 gap-2">
                          <Input
                            label="Фамилия"
                            placeholder="Иванов"
                            value={editingMemberData?.lastName || ''}
                            onChange={(e) => setEditingMemberData({
                              ...editingMemberData!,
                              lastName: e.target.value,
                            })}
                          />
                          <Input
                            label="Имя *"
                            placeholder="Иван"
                            value={editingMemberData?.firstName || ''}
                            onChange={(e) => setEditingMemberData({
                              ...editingMemberData!,
                              firstName: e.target.value,
                            })}
                          />
                        </div>
                        <Input
                          label="Telegram"
                          placeholder="@username"
                          value={editingMemberData?.username || ''}
                          onChange={(e) => setEditingMemberData({
                            ...editingMemberData!,
                            username: e.target.value,
                          })}
                        />
                        <Select
                          label="Права"
                          value={editingMemberData?.role || 'EXECUTOR'}
                          onChange={(e) => setEditingMemberData({
                            ...editingMemberData!,
                            role: e.target.value,
                          })}
                          options={roleOptions}
                        />
                        <div className="flex gap-2">
                          <Button
                            onClick={() => {
                              if (!editingMemberData) return;
                              updateMemberMutation.mutate({
                                userId: member.id,
                                data: {
                                  firstName: editingMemberData.firstName || undefined,
                                  lastName: editingMemberData.lastName || undefined,
                                  username: editingMemberData.username.replace('@', '') || undefined,
                                  position: editingMemberData.position || undefined,
                                  role: editingMemberData.role,
                                },
                              });
                            }}
                            disabled={!editingMemberData?.firstName.trim()}
                            isLoading={updateMemberMutation.isPending}
                            className="flex-1"
                            size="sm"
                          >
                            Сохранить
                          </Button>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => {
                              setEditingMemberId(null);
                              setEditingMemberData(null);
                            }}
                          >
                            Отмена
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </Card>
        </div>
      )}

      {/* Google Sheets */}
      {activeTab === 'sheets' && (
        <Card className="p-4 space-y-4">
          <div>
            <h3 className="font-medium text-tg-text mb-2">Интеграция с Google Sheets</h3>
            <p className="text-sm text-tg-hint mb-4">
              Подключите Google таблицу для автоматической синхронизации протокола.
            </p>
          </div>
          
          <Input
            label="ID Google таблицы"
            placeholder="1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs74OgvE2upms"
            value={formData.googleSheetId || project.googleSheetId || ''}
            onChange={(e) => setFormData({ ...formData, googleSheetId: e.target.value })}
            disabled={!isAdmin}
          />
          
          <p className="text-xs text-tg-hint">
            ID находится в URL таблицы: docs.google.com/spreadsheets/d/<strong>ID</strong>/edit
          </p>

          {isAdmin && (
            <div className="flex gap-2">
              <Button
                onClick={() => updateProjectMutation.mutate({ googleSheetId: formData.googleSheetId })}
                isLoading={updateProjectMutation.isPending}
              >
                Сохранить
              </Button>
              {project.googleSheetId && (
                <Button
                  variant="outline"
                  onClick={() => {
                    // TODO: Test connection
                    hapticFeedback('medium');
                  }}
                >
                  Проверить подключение
                </Button>
              )}
            </div>
          )}

          {project.googleSheetId && (
            <div className="pt-4 border-t border-tg-bg">
              <h4 className="font-medium text-tg-text mb-2">Синхронизация</h4>
              <div className="flex gap-2">
                <Button variant="secondary" size="sm">
                  📤 Экспорт в Sheets
                </Button>
                <Button variant="secondary" size="sm">
                  📥 Импорт из Sheets
                </Button>
              </div>
            </div>
          )}
        </Card>
      )}

      {/* Напоминания */}
      {activeTab === 'reminders' && (
        <Card className="p-4 space-y-4">
          <h3 className="font-medium text-tg-text">Правила напоминаний по умолчанию</h3>
          
          <div className="space-y-3">
            {[
              { id: 'on_create', label: 'При создании задачи', enabled: true },
              { id: 'before_deadline', label: 'За 1 день до дедлайна', enabled: true },
              { id: 'on_deadline', label: 'В день дедлайна', enabled: true },
              { id: 'daily_overdue', label: 'Ежедневно при просрочке', enabled: true },
            ].map((rule) => (
              <label key={rule.id} className="flex items-center justify-between p-3 bg-tg-bg rounded-xl">
                <span className="text-sm text-tg-text">{rule.label}</span>
                <input
                  type="checkbox"
                  defaultChecked={rule.enabled}
                  disabled={!isAdmin}
                  className="w-5 h-5 rounded accent-tg-button"
                />
              </label>
            ))}
          </div>

          {isAdmin && (
            <Button onClick={() => hapticFeedback('success')}>
              Сохранить правила
            </Button>
          )}
        </Card>
      )}

      {/* Настройки чек-листов */}
      {activeTab === 'checklists' && checklistSettingsForm && (
        <Card className="p-4 space-y-4">
          <h3 className="font-medium text-tg-text mb-2">Чек-листы</h3>

          {/* Приёмка */}
          <div className="space-y-2">
            <h4 className="text-sm font-semibold text-tg-text">Приёмка задач</h4>
            <label className="flex items-center justify-between p-2 bg-tg-bg rounded-lg text-sm">
              <span>Регламентные чек-листы</span>
              <input
                type="checkbox"
                className="w-5 h-5 accent-tg-button"
                checked={checklistSettingsForm.approvalRequiredRegulation}
                onChange={(e) =>
                  setChecklistSettingsForm({
                    ...checklistSettingsForm,
                    approvalRequiredRegulation: e.target.checked,
                  })
                }
              />
            </label>
            <label className="flex items-center justify-between p-2 bg-tg-bg rounded-lg text-sm">
              <span>Чек-листы мероприятий</span>
              <input
                type="checkbox"
                className="w-5 h-5 accent-tg-button"
                checked={checklistSettingsForm.approvalRequiredEvent}
                onChange={(e) =>
                  setChecklistSettingsForm({
                    ...checklistSettingsForm,
                    approvalRequiredEvent: e.target.checked,
                  })
                }
              />
            </label>
            <label className="flex items-center justify-between p-2 bg-tg-bg rounded-lg text-sm">
              <span>Личные чек-листы</span>
              <input
                type="checkbox"
                className="w-5 h-5 accent-tg-button"
                checked={checklistSettingsForm.approvalRequiredPersonal}
                onChange={(e) =>
                  setChecklistSettingsForm({
                    ...checklistSettingsForm,
                    approvalRequiredPersonal: e.target.checked,
                  })
                }
              />
            </label>
          </div>

          {/* KPI */}
          <div className="space-y-2 pt-2 border-t border-tg-bg">
            <h4 className="text-sm font-semibold text-tg-text">KPI</h4>
            <label className="flex items-center justify-between p-2 bg-tg-bg rounded-lg text-sm">
              <span>Считать \"Выполнено\" как \"Принята\"</span>
              <input
                type="checkbox"
                className="w-5 h-5 accent-tg-button"
                checked={checklistSettingsForm.kpiCountCompletedAsAccepted}
                onChange={(e) =>
                  setChecklistSettingsForm({
                    ...checklistSettingsForm,
                    kpiCountCompletedAsAccepted: e.target.checked,
                  })
                }
              />
            </label>
            <label className="flex items-center justify-between p-2 bg-tg-bg rounded-lg text-sm">
              <span>Учитывать личные задачи в KPI</span>
              <input
                type="checkbox"
                className="w-5 h-5 accent-tg-button"
                checked={checklistSettingsForm.includePersonalInKpi}
                onChange={(e) =>
                  setChecklistSettingsForm({
                    ...checklistSettingsForm,
                    includePersonalInKpi: e.target.checked,
                  })
                }
              />
            </label>
          </div>

          {/* Генерация и утренний дайджест */}
          <div className="space-y-3 pt-2 border-t border-tg-bg">
            <div>
              <label className="text-sm font-medium text-tg-text mb-1 block">
                Горизонт генерации задач (дней)
              </label>
              <input
                type="number"
                min={1}
                max={365}
                value={checklistSettingsForm.instanceGenerationHorizon}
                onChange={(e) =>
                  setChecklistSettingsForm({
                    ...checklistSettingsForm,
                    instanceGenerationHorizon: Number(e.target.value) || 1,
                  })
                }
                className="w-full p-2 border rounded-lg text-sm"
              />
            </div>

            <div>
              <label className="text-sm font-medium text-tg-text mb-1 block">
                Время утреннего дайджеста (План на сегодня)
              </label>
              <input
                type="time"
                value={checklistSettingsForm.morningDigestTime || ''}
                onChange={(e) =>
                  setChecklistSettingsForm({
                    ...checklistSettingsForm,
                    morningDigestTime: e.target.value || null,
                  })
                }
                className="w-full p-2 border rounded-lg text-sm"
              />
            </div>

            <label className="flex items-center justify-between p-2 bg-tg-bg rounded-lg text-sm">
              <div>
                <span className="font-medium">Уведомления о назначении задач</span>
                <p className="text-xs text-tg-hint mt-0.5">
                  Отправлять уведомления сотрудникам при создании новых задач
                </p>
              </div>
              <input
                type="checkbox"
                className="w-5 h-5 accent-tg-button"
                checked={checklistSettingsForm.notifyOnInstanceAssignment ?? false}
                onChange={(e) =>
                  setChecklistSettingsForm({
                    ...checklistSettingsForm,
                    notifyOnInstanceAssignment: e.target.checked,
                  })
                }
              />
            </label>
          </div>

          {/* Статус после отклонения */}
          <div className="space-y-2 pt-2 border-t border-tg-bg">
            <h4 className="text-sm font-semibold text-tg-text">Статус после отклонения</h4>
            <p className="text-xs text-tg-hint mb-2">
              Выберите, в какой статус возвращать задачу после отклонения руководителем
            </p>
            <select
              value={checklistSettingsForm.statusAfterRejection || 'REJECTED'}
              onChange={(e) =>
                setChecklistSettingsForm({
                  ...checklistSettingsForm,
                  statusAfterRejection: e.target.value,
                })
              }
              className="w-full p-2 border rounded-lg text-sm"
            >
              <option value="REJECTED">Отклонена (REJECTED)</option>
              <option value="IN_PROGRESS">В работе (IN_PROGRESS)</option>
              <option value="ASSIGNED">Назначена (ASSIGNED)</option>
            </select>
          </div>

          {/* Рабочие дни */}
          <div className="space-y-2 pt-2 border-t border-tg-bg">
            <h4 className="text-sm font-semibold text-tg-text">Рабочие дни</h4>
            <p className="text-xs text-tg-hint">
              Используются для еженедельных и ежемесячных задач с правилом \"рабочий день\".
            </p>
            <div className="grid grid-cols-4 gap-2">
              {['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'].map((label, index) => {
                const workingDays = checklistSettingsForm.workingDays || [];
                const checked = workingDays.includes(index);
                return (
                  <label
                    key={index}
                    className="flex items-center gap-1 text-xs bg-tg-bg rounded-lg px-2 py-1"
                  >
                    <input
                      type="checkbox"
                      className="w-4 h-4 accent-tg-button"
                      checked={checked}
                      onChange={(e) => {
                        const current = new Set(workingDays);
                        if (e.target.checked) {
                          current.add(index);
                        } else {
                          current.delete(index);
                        }
                        setChecklistSettingsForm({
                          ...checklistSettingsForm,
                          workingDays: Array.from(current).sort((a, b) => a - b),
                        });
                      }}
                    />
                    <span>{label}</span>
                  </label>
                );
              })}
            </div>
          </div>

          <Button
            onClick={() =>
              checklistSettingsForm &&
              checklistSettingsMutation.mutate({
                approvalRequiredRegulation: checklistSettingsForm.approvalRequiredRegulation,
                approvalRequiredEvent: checklistSettingsForm.approvalRequiredEvent,
                approvalRequiredPersonal: checklistSettingsForm.approvalRequiredPersonal,
                kpiCountCompletedAsAccepted:
                  checklistSettingsForm.kpiCountCompletedAsAccepted,
                includePersonalInKpi: checklistSettingsForm.includePersonalInKpi,
                instanceGenerationHorizon:
                  checklistSettingsForm.instanceGenerationHorizon,
                workingDays: checklistSettingsForm.workingDays || [],
                morningDigestTime: checklistSettingsForm.morningDigestTime,
                notifyOnInstanceAssignment: checklistSettingsForm.notifyOnInstanceAssignment ?? false,
                statusAfterRejection: checklistSettingsForm.statusAfterRejection || 'REJECTED',
              })
            }
            isLoading={checklistSettingsMutation.isPending}
          >
            Сохранить настройки чек-листов
          </Button>
        </Card>
      )}

      {/* Опасная зона */}
      {isAdmin && activeTab === 'general' && (
        <Card className="p-4 border-2 border-red-200">
          <div className="flex items-center gap-2 mb-3">
            <Shield className="w-5 h-5 text-red-500" />
            <h3 className="font-medium text-red-500">Опасная зона</h3>
          </div>
          <p className="text-sm text-tg-hint mb-4">
            Удаление проекта приведёт к потере всех данных, включая протоколы и задачи. Это действие нельзя отменить.
          </p>
          <Button
            variant="danger"
            size="sm"
            onClick={async () => {
              hapticFeedback('warning');
              const confirmed = await showConfirm(
                `Вы уверены, что хотите удалить проект "${project.name}"?\n\nЭто действие нельзя отменить. Все данные проекта (протоколы, задачи, чек-листы) будут потеряны.`
              );
              if (confirmed) {
                deleteProjectMutation.mutate();
              }
            }}
            isLoading={deleteProjectMutation.isPending}
            disabled={deleteProjectMutation.isPending}
          >
            <Trash2 className="w-4 h-4 mr-1" />
            Удалить проект
          </Button>
        </Card>
      )}

      {/* Версия приложения (чтобы сразу было видно, что деплой применился) */}
      <div className="pt-2 text-center text-xs text-tg-hint">
        Версия: {import.meta.env.VITE_BUILD_TIME || 'dev'}
      </div>
    </div>
  );
}

